import { UploadSimpleIcon } from "@phosphor-icons/react";
import { useT } from "@transifex/react";
import { useEffect, useMemo, useRef, useState } from "react";
import CharacterCount from "../../../basic/CharacterCount";
import InlineFormError from "../../../basic/InlineFormError";
import PhaseSixIcon from "../../../basic/phaseSixIcon/PhaseSixIcon";
import { PhosphorIcon } from "../../../basic/phosphorIcon/PhosphorIcon";
import BasePopupWrapper, { TitleColorVariants } from "../../../basic/popups/BasePopupWrapper";
import { MAX_NAME_LENGTH } from "../../../constants/LimitConstants";
import { ButtonColorVariant } from "../../teacherArea/AreaButton";
import StudentNameChip from "./StudentNameChip";
import UploadStudentListPopup from "./UploadStudentListPopup";

const NAME_SEPARATORS_REGEX = /[,;\n]/;

interface AddClassMembersPopupProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (names: string[]) => Promise<{ failedNames: string[] }>;
    existingClassMemberNames: string[];
}

const AddClassMembersPopup = (props: AddClassMembersPopupProps) => {
    const t = useT();

    const [pendingNames, setPendingNames] = useState<string[]>([]);
    const [inputValue, setInputValue] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadFile, setUploadFile] = useState<File | null>(null);

    const uploadInputRef = useRef<HTMLInputElement>(null);

    const existingNamesLower = useMemo(
        () => props.existingClassMemberNames.map((e) => e.toLowerCase()),
        [props.existingClassMemberNames]
    );
    const pendingNamesLower = pendingNames.map((n) => n.toLowerCase());
    const inputIsDuplicate =
        inputValue.trim().length > 0 &&
        (existingNamesLower.includes(inputValue.trim().toLowerCase()) ||
            pendingNamesLower.includes(inputValue.trim().toLowerCase()));

    useEffect(() => {
        if (!props.isOpen) {
            setPendingNames([]);
            setInputValue("");
            setIsSubmitting(false);
            setUploadFile(null);
        }
    }, [props.isOpen]);

    const t_title = t("Add students", { _tags: "SchoolClasses, AddClassMembersPopup" });
    const t_label = t("Names of students", { _tags: "SchoolClasses, AddClassMembersPopup" });
    const t_placeholder = t("Add names...", { _tags: "SchoolClasses, AddClassMembersPopup" });
    const t_tip = t("Tip: Separate multiple students with Enter or comma", {
        _tags: "SchoolClasses, AddClassMembersPopup",
    });
    const t_cancel = t("Cancel", { _tags: "SchoolClasses, AddClassMembersPopup" });
    const t_add = t("Add", { _tags: "SchoolClasses, AddClassMembersPopup" });
    const t_sr_removeChip = t("Remove", { _tags: "SchoolClasses, AddClassMembersPopup, ScreenReaderOnly" });
    const t_alreadyExists = (name: string) =>
        t("{name} already exists.", { _tags: "SchoolClasses, AddClassMembersPopup", name });
    const t_uploadList = t("Upload list", { _tags: "SchoolClasses, AddClassMembersPopup" });

    const confirmCurrentInput = (value: string) => {
        const trimmed = value.trim();
        if (!trimmed || inputIsDuplicate) return;
        setPendingNames([...pendingNames, trimmed]);
        setInputValue("");
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" || e.key === "," || e.key === ";") {
            e.preventDefault();
            confirmCurrentInput(inputValue);
        } else if (e.key === "Backspace" && !inputValue && pendingNames.length > 0) {
            setPendingNames(pendingNames.slice(0, -1));
        }
    };

    // Every complete segment becomes a chip, duplicates are skipped silently, and the trailing
    // segment stays in the input.
    const commitSeparatedNames = (value: string) => {
        const segments = value.split(NAME_SEPARATORS_REGEX);
        const remainder = segments.pop() ?? "";
        const updatedNames = [...pendingNames];
        const takenNamesLower = new Set([...existingNamesLower, ...updatedNames.map((n) => n.toLowerCase())]);

        segments.forEach((segment) => {
            const trimmed = segment.trim();
            if (trimmed && !takenNamesLower.has(trimmed.toLowerCase())) {
                updatedNames.push(trimmed);
                takenNamesLower.add(trimmed.toLowerCase());
            }
        });

        setPendingNames(updatedNames);
        setInputValue(remainder.trimStart());
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (NAME_SEPARATORS_REGEX.test(value)) {
            commitSeparatedNames(value);
        } else {
            setInputValue(value);
        }
    };

    // Text inputs strip newlines from pasted text, so multiline lists have to be caught here
    // before the browser flattens them into one name.
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const pastedText = e.clipboardData.getData("text");
        if (NAME_SEPARATORS_REGEX.test(pastedText)) {
            e.preventDefault();
            commitSeparatedNames(inputValue + pastedText);
        }
    };

    const handleRemoveChip = (name: string) => {
        setPendingNames(pendingNames.filter((n) => n !== name));
    };

    const handleUploadFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (file) {
            setUploadFile(file);
        }
    };

    const handleUploadSubmit = async (names: string[]) => {
        const { failedNames } = await props.onSubmit(names);
        if (failedNames.length === 0) {
            setUploadFile(null);
            props.onClose();
        } else {
            // Back to manual entry with the failed names as chips, matching the manual submit behavior.
            setPendingNames(failedNames);
            setInputValue("");
            setUploadFile(null);
        }
    };

    // Guards against a second click landing before the disabled state has rendered — each one would
    // create its own set of class members.
    const handleSubmit = async () => {
        if (isSubmitting) return;

        const allNames = [...pendingNames];
        const trimmedInput = inputValue.trim();
        if (trimmedInput && !inputIsDuplicate) {
            allNames.push(trimmedInput);
        }

        setIsSubmitting(true);
        try {
            const { failedNames } = await props.onSubmit(allNames);
            if (failedNames.length === 0) {
                props.onClose();
            } else {
                setPendingNames(failedNames.filter((n) => pendingNames.includes(n)));
                setInputValue(failedNames.includes(trimmedInput) ? inputValue : "");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (isSubmitting) return;
        props.onClose();
    };

    const renderChips = () => (
        <div className="flex flex-wrap gap-s">
            {pendingNames.map((name) => (
                <StudentNameChip
                    key={name}
                    name={name}
                    removeLabel={t_sr_removeChip}
                    onRemove={() => handleRemoveChip(name)}
                />
            ))}
        </div>
    );

    const renderInput = () => (
        <div className="flex flex-col gap-xs">
            <span className="text-l text-p6-grey-dark">{t_label}</span>
            <div className="flex flex-col gap-s border-2 border-p6-grey-lightest rounded-small-container p-s">
                {pendingNames.length > 0 && renderChips()}
                <input
                    type="text"
                    data-testid="add-class-members-input"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    placeholder={t_placeholder}
                    maxLength={MAX_NAME_LENGTH}
                    className="text-l text-p6-grey-dark bg-transparent border-none outline-none w-full placeholder:opacity-50"
                />
            </div>
            {inputValue.length > 0 && (
                <CharacterCount
                    currentLength={inputValue.length}
                    maxLength={MAX_NAME_LENGTH}
                    dataTestId="add-class-members-character-count"
                />
            )}
            {inputIsDuplicate && (
                <InlineFormError data-testid="add-class-members-duplicate-error">
                    {t_alreadyExists(inputValue.trim())}
                </InlineFormError>
            )}
            <div className="flex gap-s items-center justify-between flex-wrap">
                <div className="flex gap-xs items-center">
                    <PhaseSixIcon
                        name="info"
                        className="text-xl text-p6-grey-dark shrink-0"
                    />
                    <span className="text-m text-p6-grey-dark">{t_tip}</span>
                </div>
                <button
                    type="button"
                    onClick={() => uploadInputRef.current?.click()}
                    className="flex gap-xs items-center bg-transparent border-none cursor-pointer p-no text-m text-p6-grey-dark hover:text-p6-orange"
                >
                    {`${t_uploadList} (CSV, XLSX)`}
                    <PhosphorIcon
                        phosphorIcon={UploadSimpleIcon}
                        fontSize="text-xl"
                    />
                </button>
                <input
                    ref={uploadInputRef}
                    type="file"
                    accept=".csv,.xlsx"
                    onChange={handleUploadFileSelected}
                    className="hidden"
                    data-testid="upload-student-list-input"
                />
            </div>
        </div>
    );

    return (
        <>
            <UploadStudentListPopup
                isOpen={props.isOpen && !!uploadFile}
                file={uploadFile}
                onClose={() => setUploadFile(null)}
                onCloseAll={() => {
                    setUploadFile(null);
                    props.onClose();
                }}
                onSubmit={handleUploadSubmit}
                existingClassMemberNames={props.existingClassMemberNames}
            />
            <BasePopupWrapper
                isOpen={props.isOpen && !uploadFile}
                onClose={handleClose}
                title={t_title}
                titleColor={TitleColorVariants.DEFAULT}
                secondaryButton={{
                    text: t_cancel,
                    onClick: handleClose,
                    colorVariant: ButtonColorVariant.HIGH_CONTRAST,
                    disabled: isSubmitting,
                }}
                primaryButton={{
                    text: t_add,
                    onClick: handleSubmit,
                    disabled: (pendingNames.length === 0 && !inputValue.trim()) || inputIsDuplicate || isSubmitting,
                }}
            >
                {renderInput()}
            </BasePopupWrapper>
        </>
    );
};

export default AddClassMembersPopup;
